import { Spinner } from '@/components/Spinner';
import { cn } from '@/helpers/ui-utils';
import { Slot, Slottable } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import pulseAnimation from './animate-pulse.module.css';

const buttonVariants = cva(
  [
    'relative',
    'flex',
    'items-center',
    'justify-center',
    'gap-[0.375rem]',
    'whitespace-nowrap',
    'outline-hidden',
    'border',
    'border-solid',
    'border-transparent',
    'focus:ring-[#E5E5E5]',
    'focus:ring-offset-0',
    'focus:ring-2',
    'transition-all',
    'duration-300',
    'ease-in',
    'hover:cursor-pointer',
  ],
  {
    variants: {
      variant: {
        default: [
          'bg-primary',
          'text-primary-foreground',
          'hover:bg-primary-hover',
        ],
        'primary-muted': [
          'bg-[#FFF4F0]',

          'border-[#FFF4F0]',
          'text-primary',
          'hover:border-[#FFEAE0]',
          'hover:bg-[#FFEAE0]',
          'focus:border-[#FED8C8]',
        ],
        secondary: [
          'bg-secondary',
          'text-secondary-foreground',
          'focus:border-secondary',
          'hover:bg-secondary-hover',
          'active:border-secondary-active',
        ],
        tertiary: [
          'bg-tertiary',
          'text-tertiary-foreground',
          'border-tertiary',
          'hover:bg-tertiary-hover',
          'focus:border-tertiary-hover',
          'focus:text-tertiary-foreground',
        ],
        outline: [
          'bg-transparent',
          'text-primary',
          'border-primary',
          'active:border-primary-active',
          'hover:bg-primary/10',
          'focus:border-primary',
          'focus:ring-primary',
        ],
        'outline-primary-hover': [
          'bg-transparent',
          'text-black',
          'border-black',
          'active:border-primary-active',
          'hover:border-primary',
          'hover:text-primary',
          'focus:border-primary',
          'focus:ring-primary',
        ],
        'outline-gray': [
          'bg-transparent',
          'text-[#404040]',
          'border-[#E5E5E5]',
          'active:border-[#D4D4D4]',
          'hover:bg-[#FAFAFA]',
          'hover:border-[#D4D4D4]',
          'focus:border-[#D4D4D4]',
        ],
        link: [
          'bg-transparent',
          'text-link',
          'border-transparent',
          'hover:underline',
          'focus:border-primary',
          'focus:shadow-none',
        ],
        black: ['bg-foreground', 'text-background', 'hover:bg-foreground/90'],
        destructive: [
          'bg-destructive',
          'text-background',
          'border-destructive',
          'hover:bg-destructive-hover',
          'hover:border-destructive-hover',
          'focus:border-destructive-hover',
        ],
        positive: [
          'bg-positive',
          'text-positive-foreground',
          'border-positive',
          'hover:bg-positive-hover',
          'hover:border-positive-hover',
          'focus:border-positive-hover',
        ],
        'destructive-muted': [
          'bg-[#FEF1F3]',
          'text-destructive',
          'hover:bg-[#FEE1E7]',
          'active:border-[#FEC8D3]',
          'focus:border-[#FEC8D3]',
          'focus:ring-[#FEE2E2]',
        ],
      },
      size: {
        default: ['h-10', 'px-4', 'py-[10px]', 'text-sm', 'leading-[15px]'],
        sm: ['h-9', 'px-3', 'py-2', 'text-xs', 'leading-[14px]'],
        lg: ['h-11', 'px-5', 'py-[14px]', 'text-md', 'leading-[18px]'],
        icon: ['h-fit', 'min-h-10', 'w-fit', 'min-w-10', 'py-2', 'px-2'],
      },
      rounded: {
        default: 'rounded-sm',
        none: 'rounded-none',
        xs: 'rounded-xs',
        md: 'rounded-md',
        lg: 'rounded-lg',
        full: 'rounded-full',
      },
      pulse: {
        true: `${pulseAnimation.pulse}`,
        false: '',
      },
      busy: {
        false: null,
        true: ['hover:cursor-wait', 'opacity-50'],
      },
      weight: {
        default: ['font-[400]', 'font-nunito-sans'],
        light: ['font-[300]', 'font-nunito-sans-light'],
        semibold: ['font-[600]', 'font-nunito-sans-semibold'],
        bold: ['font-[700]', 'font-nunito-sans-bold'],
        extrabold: ['font-[800]', 'font-nunito-sans-extra-bold'],
      },
      disabled: {
        false: null,
        true: ['opacity-50', 'hover:cursor-not-allowed', 'pointer-events-auto'],
      },
      stretch: {
        false: null,
        true: 'w-full',
      },
      prefix: {
        false: null,
        true: [
          '[&_.prefix]:w-[20px]',
          '[&_.prefix]:h-[20px]',
          '[&_svg]:size-5',
          '[&_svg]:pointer-events-none',
          '[&_svg]:shrink-0',
          '[&_svg]:stroke-1',
        ],
      },
      suffix: {
        false: null,
        true: [
          '[&_.suffix]:w-[20px]',
          '[&_.suffix]:h-[20px]',
          '[&_svg]:size-5',
          '[&_svg]:pointer-events-none',
          '[&_svg]:shrink-0',
          '[&_svg]:stroke-1',
        ],
      },
    },
    compoundVariants: [
      {
        size: 'icon',
        rounded: 'default',
        className: ['rounded-xs'],
      },
      {
        size: 'icon',
        rounded: 'md',
        className: ['rounded-xs'],
      },
      {
        size: 'icon',
        rounded: 'lg',
        className: ['rounded-md'],
      },
      {
        size: 'icon',
        rounded: 'full',
        className: ['rounded-full'],
      },
      {
        prefix: true,
        variant: 'default',
        className: ['[&_svg]:stroke-primary-foreground'],
      },
      {
        prefix: true,
        size: 'sm',
        className: ['[&_.prefix]:w-4', '[&_.prefix]:h-4', '[&_svg]:size-4'],
      },
      {
        prefix: true,
        size: 'lg',
        className: ['gap-2'],
      },
      {
        suffix: true,
        variant: 'default',
        className: ['[&_svg]:stroke-primary-foreground'],
      },
      {
        suffix: true,
        size: 'sm',
        className: ['[&_.suffix]:w-4', '[&_.suffix]:h-4', '[&_svg]:size-4'],
      },
      {
        suffix: true,
        size: 'lg',
        className: ['gap-2'],
      },
      {
        disabled: true,
        busy: true,
        className: ['hover:cursor-wait'],
      },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'default',
      rounded: 'default',
      weight: 'default',
      pulse: false,
      busy: false,
      disabled: false,
      stretch: false,
    },
  },
);

type ButtonCVAProps = {
  /**
   * Visual style variant of the button. Determines background color, text color, and hover states.
   * - `default` - Primary orange button with white text
   * - `primary-muted` - Light orange background with orange text
   * - `secondary` - Secondary color scheme
   * - `tertiary` - Tertiary color scheme with border
   * - `outline` - Transparent background with primary border
   * - `outline-gray` - Transparent background with gray border
   * - `link` - Text-only button with underline on hover
   * - `black` - Black background with white text
   * - `destructive` - Red background for dangerous actions
   * - `destructive-muted` - Light red background with red text
   * - `positive` - Green background for affirmative actions
   * @default 'default'
   */
  variant?: VariantProps<typeof buttonVariants>['variant'];

  /**
   * Size variant controlling height and padding.
   * - `default` - Standard button size (h-10)
   * - `sm` - Small button (h-9)
   * - `lg` - Large button (h-11)
   * - `icon` - Square button for icons only
   * @default 'default'
   */
  size?: VariantProps<typeof buttonVariants>['size'];

  /**
   * Border radius variant. Controls corner rounding.
   * - `default` - Standard rounding (rounded-sm)
   * - `none` - No rounding
   * - `xs` - Extra small rounding
   * - `md` - Medium rounding
   * - `lg` - Large rounding
   * - `full` - Fully rounded (pill shape)
   * @default 'default'
   */
  rounded?: VariantProps<typeof buttonVariants>['rounded'];

  /**
   * Font weight of the button text.
   * - `default` - Normal weight (400)
   * - `light` - Light weight (300)
   * - `semibold` - Semibold weight (600)
   * - `bold` - Bold weight (700)
   * - `extrabold` - Extra bold weight (800)
   * @default 'default'
   */
  weight?: VariantProps<typeof buttonVariants>['weight'];

  /**
   * Enable pulsing animation for attention-grabbing effect.
   * @default false
   */
  pulse?: VariantProps<typeof buttonVariants>['pulse'];

  /**
   * Indicates loading state. Shows spinner overlay and disables interaction.
   * @default false
   */
  busy?: VariantProps<typeof buttonVariants>['busy'];
};

export interface ButtonProps
  extends
    Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'prefix' | 'suffix'>,
    ButtonCVAProps {
  /**
   * Render as child component using Radix Slot. Allows composition with other elements while maintaining button styling.
   * @default false
   */
  asChild?: boolean;

  /**
   * Disable button interaction. Makes button non-clickable and applies opacity reduction.
   * @default false
   */
  disabled?: boolean;

  /**
   * Make button take full width of container. Useful for form buttons or mobile layouts.
   * @default false
   */
  stretch?: boolean;

  /**
   * React node to display before button content. Typically icons or small components positioned on the left.
   */
  prefix?: React.ReactNode;

  /**
   * React node to display after button content. Typically icons or indicators positioned on the right.
   */
  suffix?: React.ReactNode;
}

/**
 * Trigger actions and navigation with customizable buttons that support loading states, icons, and multiple visual styles.
 *
 * @param props - Button properties
 * @param ref - Forwarded ref to the button element
 * @returns Rendered button component
 *
 * @example
 * ```tsx
 * // Basic button
 * <Button>Click me</Button>
 *
 * // Button with icon prefix
 * <Button prefix={<Icon />}>Save</Button>
 *
 * // Loading button
 * <Button busy>Loading...</Button>
 *
 * // Destructive button
 * <Button variant="destructive">Delete</Button>
 * ```
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      rounded,
      weight,
      pulse,
      busy,
      asChild,
      children,
      disabled,
      stretch,
      prefix: prefixComponent,
      suffix: suffixComponent,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';

    const variantClass = buttonVariants({
      variant,
      size,
      pulse,
      busy,
      rounded,
      weight,
      disabled,
      stretch,
      prefix: Boolean(prefixComponent),
      suffix: Boolean(suffixComponent),
      className,
    });

    return (
      <Comp
        className={cn(variantClass)}
        aria-busy={props['aria-busy'] || busy || false}
        disabled={disabled || busy || false}
        ref={ref}
        {...props}
      >
        {busy && (
          <div className="absolute inset-0 flex items-center justify-center">
            <Spinner />
          </div>
        )}

        {prefixComponent && (
          <div className={cn(['flex', 'prefix'])}>{prefixComponent}</div>
        )}

        {React.isValidElement(children) && asChild ? (
          <Slottable>
            {React.cloneElement(children as React.ReactElement<HTMLElement>, {
              className: cn('children-content', variantClass, {
                '[&>span]:visible': !busy,
                '[&>span]:invisible': busy,
              }),
            })}
          </Slottable>
        ) : (
          <span
            className={cn('children-content', {
              visible: !busy,
              invisible: busy,
            })}
          >
            {children}
          </span>
        )}

        {suffixComponent && (
          <div className={cn(['flex', 'suffix'])}>{suffixComponent}</div>
        )}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
